// Copyright ©️ 2026 eVolve MEP, LLC

import * as THREE from 'three';

import {
  BendPreviewGeometry,
  type BendPreviewSpecifications,
} from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/BendPreviewGeometry';
import { convertToRadians } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/helpers';
import type { PlacementOffset } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/Labels/buildLabelOpts';

export class FourPointSaddleGeometry extends BendPreviewGeometry {
  getDefaultDimensions() {
    // Both leg lengths must be positive: requires enough takeUpLength to accommodate all segments
    return {
      ...super.getDefaultDimensions(),
      angle: 45,
      saddleHeight: 2,
      saddleLength: 3,
      distanceToCenterOfObstruction: 5,
      takeUpLength: 10,
    };
  }

  createBendDefs(dimensions: BendPreviewSpecifications) {
    const { angle, saddleHeight, takeUpLength, distanceToCenterOfObstruction, saddleLength } = dimensions;

    const theta = convertToRadians(angle);
    const saddleLegLength = saddleHeight / Math.tan(theta);
    const hypotenuse = saddleHeight / Math.sin(theta);
    const startFlatLength = takeUpLength - distanceToCenterOfObstruction - saddleLength / 2 - saddleLegLength;
    const endFlatLength = distanceToCenterOfObstruction - saddleLength / 2 - saddleLegLength;

    // This scoots the saddle height label forward so that it sits in the midpoint of the saddle.
    const saddleHeightMidpointOffset: PlacementOffset = {
      relativeTo: 'world',
      offset: new THREE.Vector3(0, 0, saddleLength / 2),
    };

    return [
      {
        runLength: startFlatLength,
        thenBendBy: theta,
        startLabels: [
          {
            label: 'Take Up Length',
            length: takeUpLength,
            offsets: [{ relativeTo: 'world', offset: 'down' }],
          },
        ],
        endLabels: [
          {
            label: 'Angle',
            angle,
            radius: Math.min(1, saddleLegLength * 0.75),
            offsets: [{ relativeTo: 'world', offset: 'down' }],
          },
        ],
      },
      { runLength: hypotenuse, thenRotateBy: Math.PI, thenBendBy: theta },
      {
        runLength: saddleLength,
        thenBendBy: theta,
        startLabels: [
          { label: 'Saddle Length', length: saddleLength, offsets: [{ relativeTo: 'world', offset: 'down' }] },
          {
            label: 'Saddle Height',
            length: saddleHeight,
            isVertical: true,
            offsets: [{ relativeTo: 'world', offset: 'down' }, saddleHeightMidpointOffset],
          },
        ],
      },
      { runLength: hypotenuse, thenRotateBy: Math.PI, thenBendBy: theta },
      {
        runLength: endFlatLength,
        endLabels: [
          {
            label: 'Distance to Center of Obstruction',
            isReverse: true,
            length: distanceToCenterOfObstruction,
            directionPreference: 'inward',
            preferredDistance: 0.2,
            offsets: [{ relativeTo: 'world', offset: 'down' }],
          },
        ],
      },
    ] as const;
  }
}
